import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Col, Descriptions, Form, Input, InputNumber, message, Modal, Row,
  Select, Space, Switch, Tag, Typography
} from 'antd';
import { LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { uomApi } from './uomApi';
import {
  MEASUREMENT_TYPE_OPTIONS, MEASUREMENT_TYPE_COLOR
} from './uomConstants';
import '../../../../styles/erpModal.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — UOM Master modal (Stage 6B.8A-1B compact polish).
//
// Three modes — all share the same 760px-wide centered modal:
//   - 'add'  → empty form, every field editable.
//   - 'edit' → pre-filled. uomCode editable only for non-system rows.
//              For is_system=true rows, uomCode / uqcCode / uqcName /
//              measurementType / isGstStandard are LOCKED (server
//              rejects them; UI disables the inputs).
//   - 'view' → read-only. Renders an AntD Descriptions panel instead
//              of a wall of disabled inputs.
//
// GSTN UQC is split from the operator-facing UOM:
//   - uomCode  is what the operator picks on Item Master / invoice
//              lines (e.g. "MM", "RFT").
//   - uqcCode  is the GSTR-1 / e-invoice value rolled up from the
//              operator unit (e.g. MM → MTR, RFT → OTH).
// ─────────────────────────────────────────────────────────────────

const MEASUREMENT_TYPE_LABEL = Object.fromEntries(
  MEASUREMENT_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

const initialForm = () => ({
  uomCode:          '',
  uomName:          '',
  uomSymbol:        '',
  uqcCode:          '',
  uqcName:          '',
  measurementType:  'quantity',
  decimalPrecision: 3,
  isGstStandard:    true,
  sortOrder:        null,
  remarks:          '',
  isActive:         true
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Section title — small caps, mid-grey, tight margin.
function SectionTitle({ children, extra }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      margin: '2px 0 6px', borderBottom: '1px solid #f0f0f0', paddingBottom: 4
    }}>
      <span style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: '#6b7280'
      }}>{children}</span>
      {extra ? <span style={{ fontSize: 11, color: '#6b7280' }}>{extra}</span> : null}
    </div>
  );
}

// Inline help row — one-line note, never an Alert block.
function InlineHint({ icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      fontSize: 11.5, color: '#6b7280', lineHeight: 1.5,
      margin: '-2px 0 8px'
    }}>
      {icon ? <span style={{ marginTop: 1 }}>{icon}</span> : null}
      <span>{children}</span>
    </div>
  );
}

export default function UomModal({
  open, mode = 'add', record = null, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const isAdd = mode === 'add';
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const isSystem = (isEdit || isView) && record?.isSystem === true;
  const codeLocked = isView || isSystem;
  const uqcLocked  = isView || isSystem;
  const typeLocked = isView || isSystem;

  useEffect(() => {
    if (!open) return;
    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(initialForm());
    } else {
      form.setFieldsValue({
        uomCode:          record.uomCode || '',
        uomName:          record.uomName || '',
        uomSymbol:        record.uomSymbol || '',
        uqcCode:          record.uqcCode || '',
        uqcName:          record.uqcName || '',
        measurementType:  record.measurementType || 'quantity',
        decimalPrecision: Number.isFinite(record.decimalPrecision) ? record.decimalPrecision : 3,
        isGstStandard:    record.isGstStandard !== false,
        sortOrder:        Number.isFinite(record.sortOrder) ? record.sortOrder : null,
        remarks:          record.remarks || '',
        isActive:         record.isActive !== false
      });
    }
  }, [open, mode, record, form]);

  const onSubmit = useCallback(async () => {
    if (isView) return onClose?.();
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) {
      return;
    }
    setBusy(true);
    try {
      if (isEdit && record?.id) {
        // System rows: server rejects pinned fields. The UI disables
        // them so they don't go up the wire on a system edit; non-
        // system rows can rename code and remap UQC freely.
        const payload = {
          uomName:          values.uomName,
          uomSymbol:        values.uomSymbol || null,
          decimalPrecision: values.decimalPrecision,
          sortOrder:        values.sortOrder,
          remarks:          values.remarks || null,
          isActive:         values.isActive !== false
        };
        if (!isSystem) {
          payload.uomCode         = values.uomCode;
          payload.uqcCode         = values.uqcCode;
          payload.uqcName         = values.uqcName;
          payload.measurementType = values.measurementType;
          payload.isGstStandard   = !!values.isGstStandard;
        }
        await uomApi.update(record.id, payload);
        message.success('UOM updated.');
      } else {
        const payload = {
          uomCode:          String(values.uomCode || '').trim(),
          uomName:          String(values.uomName || '').trim(),
          uomSymbol:        values.uomSymbol || null,
          uqcCode:          String(values.uqcCode || '').trim().toUpperCase(),
          uqcName:          String(values.uqcName || '').trim(),
          measurementType:  values.measurementType,
          decimalPrecision: values.decimalPrecision,
          isGstStandard:    !!values.isGstStandard,
          sortOrder:        values.sortOrder,
          remarks:          values.remarks || null,
          isActive:         values.isActive !== false
        };
        await uomApi.create(payload);
        message.success('UOM created.');
      }
      onSaved?.();
    } catch (e) {
      message.error(unwrapApiError(e, isEdit
        ? 'Failed to update UOM.'
        : 'Failed to create UOM.'));
    } finally {
      setBusy(false);
    }
  }, [form, isEdit, isView, isSystem, record, onClose, onSaved]);

  // Compact header — title left, status chips right.
  const titleNode = useMemo(() => {
    const headLabel = isAdd
      ? 'New UOM'
      : `UOM — ${record?.uomCode || ''}`;
    const chips = [];
    if (record?.isSystem) {
      chips.push(
        <Tag key="system" icon={<LockOutlined />} color="default" style={{ marginInlineEnd: 0 }}>
          System
        </Tag>);
    }
    if (record) {
      chips.push(
        <Tag key="active" color={record.isActive === false ? 'default' : 'success'}
          style={{ marginInlineEnd: 0 }}>
          {record.isActive === false ? 'Inactive' : 'Active'}
        </Tag>);
      chips.push(
        <Tag key="gst" color={record.isGstStandard === false ? 'warning' : 'blue'}
          style={{ marginInlineEnd: 0 }}>
          {record.isGstStandard === false ? 'Internal' : 'GST Standard'}
        </Tag>);
    }
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, paddingRight: 28 /* room for AntD's close X */
      }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{headLabel}</span>
        {chips.length ? (
          <Space size={6} wrap>{chips}</Space>
        ) : null}
      </div>
    );
  }, [isAdd, record]);

  // ─── VIEW MODE — compact Descriptions panel ─────────────────────
  if (isView) {
    const lockHint = isSystem ? (
      <InlineHint icon={<LockOutlined style={{ color: '#6b7280' }} />}>
        Seeded system UOM. Code, GST UQC, and measurement type are locked
        on this row.
      </InlineHint>
    ) : null;

    const measLabel = record?.measurementType
      ? (
        <Tag color={MEASUREMENT_TYPE_COLOR[record.measurementType] || 'default'}
          style={{ marginInlineEnd: 0 }}>
          {MEASUREMENT_TYPE_LABEL[record.measurementType] || record.measurementType}
        </Tag>
      )
      : '—';

    return (
      <Modal
        open={open}
        title={titleNode}
        onCancel={onClose}
        onOk={onClose}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={760}
        centered
        destroyOnClose
        maskClosable
        className="erp-enterprise-modal"
      >
        {lockHint}

        <SectionTitle>Basic</SectionTitle>
        <Descriptions size="small" column={2} bordered
          labelStyle={{ width: 140, fontSize: 12, color: '#6b7280', background: '#fafafa' }}
          contentStyle={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}
          style={{ marginBottom: 10 }}>
          <Descriptions.Item label="UOM Code">
            {record?.uomCode
              ? <code style={{ fontWeight: 600 }}>{record.uomCode}</code>
              : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Symbol">
            {record?.uomSymbol || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="UOM Name" span={2}>
            {record?.uomName || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Measurement">{measLabel}</Descriptions.Item>
          <Descriptions.Item label="Decimal Precision">
            {Number.isFinite(record?.decimalPrecision) ? record.decimalPrecision : '—'}
          </Descriptions.Item>
        </Descriptions>

        <SectionTitle extra={record?.isGstStandard === false ? 'Internal — rolls up to UQC for filing' : null}>
          GST UQC Mapping
        </SectionTitle>
        <Descriptions size="small" column={2} bordered
          labelStyle={{ width: 140, fontSize: 12, color: '#6b7280', background: '#fafafa' }}
          contentStyle={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}
          style={{ marginBottom: 10 }}>
          <Descriptions.Item label="GST UQC Code">
            {record?.uqcCode
              ? <code style={{ fontWeight: 600 }}>{record.uqcCode}</code>
              : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="UQC Name">
            {record?.uqcName || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="GST Standard" span={2}>
            {record?.isGstStandard === false
              ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>Internal — rolls up to {record?.uqcCode}</Tag>
              : <Tag color="blue" style={{ marginInlineEnd: 0 }}>Direct GSTN UQC</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <SectionTitle>Control</SectionTitle>
        <Descriptions size="small" column={2} bordered
          labelStyle={{ width: 140, fontSize: 12, color: '#6b7280', background: '#fafafa' }}
          contentStyle={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>
          <Descriptions.Item label="Sort Order">
            {Number.isFinite(record?.sortOrder) ? record.sortOrder : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Active">
            {record?.isActive === false
              ? <Tag color="default" style={{ marginInlineEnd: 0 }}>Inactive</Tag>
              : <Tag color="success" style={{ marginInlineEnd: 0 }}>Active</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Remarks" span={2}>
            {record?.remarks
              ? <span style={{ whiteSpace: 'pre-wrap' }}>{record.remarks}</span>
              : <Text type="secondary">—</Text>}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    );
  }

  // ─── ADD / EDIT MODE — compact form ─────────────────────────────
  return (
    <Modal
      open={open}
      title={titleNode}
      onCancel={onClose}
      onOk={onSubmit}
      okText={isEdit ? 'Save' : 'Create'}
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-enterprise-modal"
    >
      {isSystem ? (
        <InlineHint icon={<LockOutlined style={{ color: '#6b7280' }} />}>
          Seeded system UOM. Code, GST UQC, and measurement type are locked;
          you can still rename, edit the symbol, precision, sort order, remarks
          and active flag.
        </InlineHint>
      ) : null}

      <Form
        form={form}
        layout="vertical"
        size="middle"
        requiredMark="optional"
        className="uom-compact-form erp-enterprise-form"
      >
        {/* Inline CSS — local-only, scoped via className. Tightens
            AntD Form.Item rhythm without a separate stylesheet. */}
        <style>{`
          .uom-compact-form .ant-form-item { margin-bottom: 10px; }
          .uom-compact-form .ant-form-item-label { padding-bottom: 2px; }
          .uom-compact-form .ant-form-item-label > label { font-size: 12px; color: #475569; height: 22px; }
          .uom-compact-form .ant-input,
          .uom-compact-form .ant-input-number-input,
          .uom-compact-form .ant-select-selector { font-size: 13px; }
          .uom-compact-form .ant-form-item-explain { font-size: 11.5px; min-height: 16px; }
        `}</style>

        <SectionTitle>Basic</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="UOM Code"
              name="uomCode"
              rules={[
                { required: true, message: 'Required' },
                { max: 20, message: 'Max 20 chars' },
                { pattern: /^[A-Za-z0-9_-]+$/, message: 'A-Z, 0-9, _, -' }
              ]}>
              <Input
                maxLength={20}
                disabled={codeLocked}
                placeholder="e.g. SQM"
                suffix={codeLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : null}
                onChange={(e) => form.setFieldsValue({
                  uomCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item
              label="UOM Name"
              name="uomName"
              rules={[
                { required: true, message: 'Required' },
                { max: 80, message: 'Max 80 chars' }
              ]}>
              <Input maxLength={80} placeholder="e.g. Square Meter" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Symbol" name="uomSymbol">
              <Input maxLength={20} placeholder="e.g. m²" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={10}>
            <Form.Item
              label="Measurement Type"
              name="measurementType"
              rules={[{ required: true, message: 'Required' }]}>
              <Select
                disabled={typeLocked}
                size="middle"
                suffixIcon={typeLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : undefined}
                options={MEASUREMENT_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Tag color={MEASUREMENT_TYPE_COLOR[o.value]}
                        style={{ marginInlineEnd: 6 }}>
                        {o.label}
                      </Tag>
                    </span>
                  )
                }))}
                placeholder="Select…"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              label="Decimals"
              name="decimalPrecision"
              rules={[{ required: true, message: 'Required' }]}
              tooltip="Decimal places used when capturing quantities. NOS / PCS use 0.">
              <InputNumber min={0} max={6} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>GST UQC Mapping</SectionTitle>
        <InlineHint icon={<InfoCircleOutlined style={{ color: '#6b7280' }} />}>
          GST reporting uses the <strong>UQC code</strong>. Internal units like
          <code style={{ margin: '0 4px' }}>MM</code> or
          <code style={{ margin: '0 4px' }}>RFT</code> map to
          <code style={{ margin: '0 4px' }}>MTR</code> /
          <code style={{ margin: '0 4px' }}>OTH</code>. Confirm with your CA
          before e-invoice use.
        </InlineHint>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={6}>
            <Form.Item
              label="UQC Code"
              name="uqcCode"
              rules={[
                { required: true, message: 'Required' },
                { pattern: /^[A-Za-z0-9]{3}$/, message: '3 chars: A-Z / 0-9' }
              ]}>
              <Input
                maxLength={3}
                disabled={uqcLocked}
                placeholder="e.g. SQM"
                style={{ textTransform: 'uppercase' }}
                suffix={uqcLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : null}
                onChange={(e) => form.setFieldsValue({
                  uqcCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="UQC Name"
              name="uqcName"
              rules={[
                { required: true, message: 'Required' },
                { max: 80, message: 'Max 80 chars' }
              ]}>
              <Input maxLength={80} disabled={uqcLocked}
                placeholder="e.g. Square Meters" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              label="GST Standard"
              name="isGstStandard"
              valuePropName="checked"
              tooltip="ON when the UOM IS a direct GSTN UQC. OFF for internal-only units.">
              <Switch disabled={uqcLocked} />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>Control</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Sort Order" name="sortOrder"
              tooltip="Lower numbers appear first in pickers.">
              <InputNumber min={0} max={999999} precision={0}
                style={{ width: '100%' }} placeholder="e.g. 10" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked"
              tooltip="Inactive UOMs hide from pickers but stay readable on history.">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24}>
            <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} maxLength={2000}
                placeholder="Optional notes — e.g. mapping rationale" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
